import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Flag, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

// Real report flow: reason options + free text, stored via POST /api/reports,
// reviewed in the admin Reports queue. Guests are sent to log in first.
const REASONS = [
  { value: "scam", label: "Scam or fraud", desc: "Asks for deposits before viewing, too good to be true" },
  { value: "misleading", label: "Misleading listing", desc: "Photos or details do not match the real unit" },
  { value: "inappropriate", label: "Inappropriate content", desc: "Offensive photos or text" },
  { value: "spam", label: "Spam or duplicate", desc: "Posted many times or not a real rental" },
  { value: "harassment", label: "Harassment", desc: "Abusive behavior from the lister" },
  { value: "other", label: "Something else", desc: "Tell us more below" },
];

export default function ReportDialog({ open, onClose, listingId, listingTitle }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    setReason(null);
    setDetails("");
    setDone(false);
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function submit() {
    if (!user) {
      onClose();
      navigate("/auth/login", { state: { from: `/listing/${listingId}` } });
      return;
    }
    if (!reason) {
      toast.warning("Pick a reason first.");
      return;
    }
    if (reason === "other" && !details.trim()) {
      toast.warning("Tell us what happened so our team can act on it.");
      return;
    }
    setSending(true);
    try {
      await api.post("/reports", {
        target_type: "listing",
        target_id: listingId,
        reason,
        details: details.trim() || null,
      });
      setDone(true);
    } catch (error) {
      toast.warning(formatApiError(error));
    } finally {
      setSending(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Report this listing"
            className="relative max-h-[88vh] w-full overflow-y-auto rounded-t-3xl bg-white p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-ambient sm:max-w-md sm:rounded-3xl sm:pb-6"
          >
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="absolute right-3 top-3 rounded-full p-2 text-brand-secondary transition hover:bg-neutral-100 hover:text-brand-ink"
            >
              <X size={18} />
            </button>

            {done ? (
              <div className="py-6 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-tint text-brand-lime">
                  <CheckCircle2 size={26} strokeWidth={2} />
                </span>
                <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-brand-ink">
                  Report received
                </h2>
                <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-brand-secondary">
                  Thank you for keeping SwipRent safe. Our team reviews every report and takes action when needed.
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-6 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-neutral-100 text-brand-ink">
                  <Flag size={20} strokeWidth={2} />
                </span>
                <h2 className="mt-3 font-display text-xl font-semibold tracking-tight text-brand-ink">
                  Report this listing
                </h2>
                <p className="mt-1 text-sm text-brand-secondary">
                  {listingTitle ? `Your report about "${listingTitle}" stays private.` : "Your report stays private."} The lister never sees who reported.
                </p>

                <div className="mt-4 space-y-2">
                  {REASONS.map((r) => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setReason(r.value)}
                      className={cn(
                        "w-full rounded-2xl border p-3.5 text-left transition",
                        reason === r.value
                          ? "border-brand-primary bg-brand-tint"
                          : "border-brand-line bg-white hover:border-slate-300"
                      )}
                    >
                      <p className="text-sm font-semibold text-brand-ink">{r.label}</p>
                      <p className="mt-0.5 text-xs text-brand-secondary">{r.desc}</p>
                    </button>
                  ))}
                </div>

                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value.slice(0, 2000))}
                  rows={3}
                  placeholder="Anything else our team should know? (optional)"
                  className="mt-4 w-full resize-none rounded-2xl border border-brand-line bg-white px-4 py-3 text-sm text-brand-ink outline-none transition placeholder:text-slate-400 focus:border-brand-primary"
                />

                <button
                  type="button"
                  onClick={submit}
                  disabled={sending}
                  className="mt-4 w-full rounded-full bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-primary-hover disabled:opacity-60"
                >
                  {sending ? "Sending" : user ? "Send report" : "Log in to report"}
                </button>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
